import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, History, Gift, Check, MessageCircle } from "lucide-react";
import api from "../api";

const br = (iso) => new Date(iso).toLocaleDateString("pt-BR");
const STATUS = { active: "Válido", used: "Usado", expired: "Vencido" };

const RewardsList = ({ items, onUse, onNotify }) => (
  <div className="mt-5" data-testid="admin-rewards">
    <p className="flex items-center gap-2 font-serif text-lg mb-2"><Gift size={14}/> Cupons de prêmio</p>
    <ul className="divide-y divide-[var(--line)] text-sm">
      {items.map(c => (
        <li key={c.coupon_id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`reward-${c.coupon_id}`}>
          <span><b className="font-mono">{c.code}</b> · {c.user_name} · {c.prize} · até {br(c.expires_at)}</span>
          {c.status === "active"
            ? <span className="flex gap-2">
                <button onClick={() => onNotify(c)} className={`${c.notified_at ? "btn-ghost" : "btn-gold"} !py-1 !px-3 text-xs inline-flex items-center gap-1`} data-testid={`reward-notify-${c.coupon_id}`}>
                  {c.notified_at ? <><Check size={12}/> Avisada</> : <><MessageCircle size={12}/> Avisar no WhatsApp</>}
                </button>
                <button onClick={() => onUse(c)} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid={`reward-use-${c.coupon_id}`}><Check size={12}/> Marcar como usado</button>
              </span>
            : <span className="tag-sun" data-testid={`reward-status-${c.coupon_id}`}>{STATUS[c.status]}</span>}
        </li>
      ))}
    </ul>
  </div>
);

export const RankingAdmin = ({ onReset }) => {
  const [history, setHistory] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [prize, setPrize] = useState("");
  const load = () => {
    api.get("/admin/referrals/history").then(r => setHistory(r.data)).catch(() => {});
    api.get("/admin/rewards").then(r => setRewards(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const reset = async () => {
    const msg = prize.trim()
      ? `Resetar o ranking? A 1ª colocada ganha o cupom "${prize.trim()}" (válido 30 dias), o top 10 fica salvo e os pontos voltam a zero.`
      : "Resetar SEM prêmio? O top 10 fica salvo e os pontos voltam a zero. (Escreva um prêmio para gerar o cupom da campeã.)";
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post("/admin/referrals/reset", { prize });
      toast.success(data.coupon ? `Ranking resetado! Cupom ${data.coupon.code} criado para ${data.coupon.user_name}` : "Ranking resetado. Novo mês começou!");
      setPrize(""); load(); onReset();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao resetar o ranking"); }
  };
  const notify = async (c) => {
    const phone = String(c.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Cliente sem telefone cadastrado"); return; }
    const msg = `Parabéns, ${(c.user_name || "").split(" ")[0]}! Você foi a embaixadora do mês ${c.period.split("-").reverse().join("/")} no Arya Studio K&C! Seu prêmio: ${c.prize}. Código: ${c.code} (válido até ${br(c.expires_at)}). Mostre no estúdio para resgatar!`;
    window.open(`https://wa.me/${phone.startsWith("55") ? phone : "55" + phone}?text=${encodeURIComponent(msg)}`, "_blank");
    await api.post(`/admin/rewards/${c.coupon_id}/notified`).catch(() => {});
    load();
  };
  const useReward = async (c) => {
    if (!window.confirm(`Marcar o cupom ${c.code} de ${c.user_name} como usado?`)) return;
    try { await api.post(`/admin/rewards/${c.coupon_id}/use`); toast.success("Cupom marcado como usado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  return (
    <div className="card-arya p-6 mb-8 border-2 border-dashed border-[var(--sun-500)]" data-testid="ranking-admin">
      <p className="text-xs tracking-widest font-display text-[color:var(--sun-700)]">ÁREA DO ESTÚDIO</p>
      <p className="text-sm text-[color:var(--ink-500)] mb-3">Só você vê esta caixa.</p>
      <div className="flex flex-wrap items-center gap-3">
        <input value={prize} onChange={e => setPrize(e.target.value)} placeholder='Prêmio da campeã (ex.: "1 Spa dos Pés grátis")'
               className="flex-1 min-w-[220px] rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="input-prize"/>
        <button onClick={reset} className="btn-gold inline-flex items-center gap-2" data-testid="btn-reset-ranking"><RotateCcw size={14}/> Resetar ranking</button>
      </div>
      {rewards.length > 0 && <RewardsList items={rewards} onUse={useReward} onNotify={notify}/>}
      {history.length > 0 && (
        <div className="mt-5" data-testid="ranking-history">
          <p className="flex items-center gap-2 font-serif text-lg mb-2"><History size={14}/> Rankings anteriores</p>
          <div className="space-y-3">
            {history.map(h => (
              <details key={h.ranking_id} className="rounded-xl bg-[var(--cream-100)] px-4 py-2" data-testid={`ranking-${h.ranking_id}`}>
                <summary className="cursor-pointer text-sm font-display">Mês {h.period.split("-").reverse().join("/")} · resetado em {br(h.created_at)}{h.coupon ? ` · prêmio: ${h.coupon.prize}` : ""}</summary>
                {h.top.length === 0 ? <p className="text-xs text-[color:var(--ink-500)] py-2">Sem pontuação neste período.</p> : (
                  <ol className="text-sm py-2 space-y-1">
                    {h.top.map((c, i) => <li key={c.user_id}>{i + 1}. {c.name} · <b>{c.referral_points} pts</b></li>)}
                  </ol>
                )}
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
